import React, { useEffect, useMemo, useState } from 'react';
import { launchSegmentCampaign, previewSegment } from '../api/adminApi';
import {
  RenderPreviewBody,
  sendTestCampaignEmail,
} from '../api/campaignsApi';
import { CampaignLaunchBody, CampaignSummary, SegmentDescription, SegmentPreview } from '../types';
import { useUser } from '../../../components/login/UserContext';
import AiCampaignAssistant, { AiCampaignAssistantDraft } from './AiCampaignAssistant';
import EmailPreviewPanel from './EmailPreviewPanel';
import ModalShell from './ModalShell';
import {
  CAMPAIGN_SENDER_DOMAIN,
  expandCampaignFromPreview,
  senderShape,
} from '../utils/campaignSender';

interface StartCampaignModalProps {
  segment: SegmentDescription;
  onClose: () => void;
  onLaunched: (campaign: CampaignSummary) => void;
  /**
   * Override how the campaign is launched. When omitted (default
   * segment flow) the modal calls
   * {@link launchSegmentCampaign}({@code segment.key}, body). The
   * Overview's "Active now" launcher passes a closure that hits
   * the live-cohort endpoint instead, so the same modal can drive
   * either a segment-keyed campaign or an ad-hoc live blast without
   * forking the UI.
   */
  launch?: (body: CampaignLaunchBody) => Promise<CampaignSummary | null>;
  /**
   * If provided, the modal skips its default {@link previewSegment}
   * fetch and renders this preview as-is. Used by the live-cohort
   * launcher, which already has the active-user list cached on the
   * page and doesn't want a second round trip (and doesn't have a
   * persistable segment key for the preview endpoint anyway).
   */
  initialPreview?: SegmentPreview | null;
  /** Skip the default segment preview fetch entirely (e.g. for ad-hoc audiences). */
  skipDefaultPreview?: boolean;
}

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type TabId = 'compose' | 'preview';

const StartCampaignModal: React.FC<StartCampaignModalProps> = ({
  segment,
  onClose,
  onLaunched,
  launch,
  initialPreview,
  skipDefaultPreview,
}) => {
  const { user } = useUser();
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaPath, setCtaPath] = useState('');
  const [preheader, setPreheader] = useState('');
  const [fromAddress, setFromAddress] = useState('');

  const [preview, setPreview] = useState<SegmentPreview | null>(initialPreview ?? null);
  const [showEmails, setShowEmails] = useState(false);

  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [testAddress, setTestAddress] = useState<string>(user?.email ?? '');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Mobile-first: collapse the preview into its own tab on phones so
  // the compose form gets the full viewport. On tablet+ we render
  // them side-by-side and `activeTab` only controls focus, not
  // visibility (handled by responsive classes below).
  const [activeTab, setActiveTab] = useState<TabId>('compose');

  const fromPreview = useMemo(
    () => expandCampaignFromPreview(fromAddress),
    [fromAddress],
  );
  const fromShape = useMemo(() => senderShape(fromAddress), [fromAddress]);

  useEffect(() => {
    if (skipDefaultPreview) return;
    let mounted = true;
    (async () => {
      const p = await previewSegment(segment.key, 20);
      if (mounted) setPreview(p);
    })();
    return () => {
      mounted = false;
    };
  }, [segment.key, skipDefaultPreview]);

  const canSubmit =
    subject.trim().length > 0 &&
    bodyText.trim().length > 0 &&
    fromShape !== 'invalid' &&
    !launching;

  // Shared payload for both the live preview iframe and the test-send.
  // Memoized so the EmailPreviewPanel's effect doesn't refire on every
  // keystroke unrelated to render-affecting fields. We pass the raw
  // (untrimmed) text-shape since the panel skips the render call when
  // either subject or body is empty — letting the user see exactly
  // what they typed, not a placeholder.
  const previewBody: RenderPreviewBody = useMemo(
    () => ({
      subject: subject.trim(),
      bodyText: bodyText.trim(),
      ctaText: ctaText.trim() || undefined,
      ctaPath: ctaPath.trim() || undefined,
      preheader: preheader.trim() || undefined,
      fromAddress: fromShape === 'invalid' ? undefined : fromAddress.trim() || undefined,
      recipientEmail: user?.email || undefined,
    }),
    [subject, bodyText, ctaText, ctaPath, preheader, fromAddress, fromShape, user?.email],
  );

  const handleTestSend = async () => {
    if (!canSubmit) return;
    const addr = testAddress.trim();
    if (!addr || !EMAIL_PATTERN.test(addr)) {
      setTestResult({ ok: false, message: 'Enter a valid email address for the test send.' });
      return;
    }
    setTestSending(true);
    setTestResult(null);
    try {
      const result = await sendTestCampaignEmail({
        ...previewBody,
        testAddress: addr,
      });
      if (result && result.successes > 0) {
        setTestResult({ ok: true, message: `Test sent to ${addr}.` });
      } else {
        const firstErr = result?.failures?.[0]?.error ?? 'Send failed';
        setTestResult({ ok: false, message: firstErr });
      }
    } catch (e) {
      setTestResult({ ok: false, message: e instanceof Error ? e.message : 'Test send failed' });
    } finally {
      setTestSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const recipientCount = preview?.totalRecipients ?? segment.recipientCount;
    const senderLine = fromPreview
      ? `\n\nSending as: ${fromPreview}`
      : '';
    const confirmed = window.confirm(
      `Launch campaign "${subject}" to ~${recipientCount.toLocaleString()} recipients in segment "${segment.label}"?` +
        senderLine +
        `\n\nSends are throttled by the daily campaign limit; suppressed users are dropped at send time.`,
    );
    if (!confirmed) return;

    try {
      setLaunching(true);
      setError(null);
      const launchBody: CampaignLaunchBody = {
        subject: subject.trim(),
        bodyText: bodyText.trim(),
        ctaText: ctaText.trim() || undefined,
        ctaPath: ctaPath.trim() || undefined,
        preheader: preheader.trim() || undefined,
        fromAddress: fromAddress.trim() || undefined,
      };
      const result = launch
        ? await launch(launchBody)
        : await launchSegmentCampaign(segment.key, launchBody);
      if (!result) throw new Error('Empty response from server');
      onLaunched(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to launch campaign');
    } finally {
      setLaunching(false);
    }
  };

  const recipientCount = preview?.totalRecipients ?? segment.recipientCount;

  return (
    <ModalShell
      open
      onClose={onClose}
      busy={launching}
      size="xl"
      eyebrow="New campaign"
      title="Launch email campaign"
      description={
        <>
          Audience:{' '}
          <span className="font-medium text-gray-700">{segment.label}</span> ·{' '}
          {recipientCount.toLocaleString()} recipients
        </>
      }
      headerEnd={
        <div className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-2 py-1 leading-tight max-w-[260px]">
          Sends through the daily-throttled, opt-out-aware Contact Email engine. Legacy n8n broadcast is not used.
        </div>
      }
      tabs={
        <div className="lg:hidden flex gap-1 -mx-1">
          {(['compose', 'preview'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`relative flex-1 py-2.5 text-sm font-medium transition-colors
                ${
                  activeTab === t
                    ? 'text-[#0b3a55]'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
            >
              {t === 'compose' ? 'Compose' : 'Preview'}
              {activeTab === t && (
                <span className="absolute left-3 right-3 bottom-0 h-0.5 bg-blue-600 rounded-t-full" />
              )}
            </button>
          ))}
        </div>
      }
      footer={
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-500 order-2 sm:order-1">
            {fromPreview && fromShape !== 'invalid' && (
              <span className="hidden md:inline">
                Sending as:{' '}
                <code className="text-[10px] text-gray-700">{fromPreview}</code>
              </span>
            )}
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-2 order-1 sm:order-2">
            <button
              type="button"
              onClick={onClose}
              disabled={launching}
              className="px-4 py-2.5 sm:py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-4 py-2.5 sm:py-2 bg-gradient-to-b from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg shadow-sm hover:from-blue-700 hover:to-blue-800 disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-600 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {launching ? 'Launching…' : `Launch to ${recipientCount.toLocaleString()}`}
            </button>
          </div>
        </div>
      }
      contentFullBleed
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] divide-x divide-gray-100 h-full">
        {/* ── Compose column ───────────────────────────────────────── */}
        <form
          onSubmit={handleSubmit}
          className={`px-5 sm:px-6 py-5 space-y-5 ${
            activeTab === 'compose' ? 'block' : 'hidden'
          } lg:block`}
        >
          <AiCampaignAssistant
            currentDraft={{ subject, preheader, bodyText, ctaText, ctaPath, fromAddress }}
            onApply={(d: AiCampaignAssistantDraft) => {
              if (d.subject !== undefined) setSubject(d.subject ?? '');
              if (d.preheader !== undefined) setPreheader(d.preheader ?? '');
              if (d.bodyText !== undefined) setBodyText(d.bodyText ?? '');
              if (d.ctaText !== undefined) setCtaText(d.ctaText ?? '');
              if (d.ctaPath !== undefined) setCtaPath(d.ctaPath ?? '');
              // Only adopt the model's sender suggestion when the admin
              // hasn't already typed one — never silently overwrite.
              if (!fromAddress.trim() && d.fromAddress) setFromAddress(d.fromAddress);
            }}
            audienceHint={segment.label}
            audienceSize={recipientCount}
            disabled={launching}
          />

          <Field label="Subject" required>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full h-11 px-3.5 text-base sm:text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow"
              placeholder="e.g. Quick check-in from BookieBrawler"
              maxLength={200}
              required
            />
          </Field>

          <Field
            label="Preheader"
            hint="Inbox preview text. Shown next to the subject in most clients."
          >
            <input
              type="text"
              value={preheader}
              onChange={(e) => setPreheader(e.target.value)}
              className="w-full h-11 px-3.5 text-base sm:text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow"
              placeholder="Drop a quick hello — keep it under 90 chars."
              maxLength={200}
            />
          </Field>

          <Field
            label="From sender"
            hint={
              <>first name or <code className="text-[10px]">@{CAMPAIGN_SENDER_DOMAIN}</code></>
            }
          >
            <input
              type="text"
              value={fromAddress}
              onChange={(e) => setFromAddress(e.target.value)}
              className={`w-full h-11 px-3.5 text-base sm:text-sm text-gray-900 placeholder-gray-400 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow ${
                fromShape === 'invalid' ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Marshall"
              maxLength={255}
            />
            <div className="mt-1.5 text-[11px]">
              {fromPreview && fromShape !== 'invalid' && (
                <span className="text-emerald-700">
                  Will send as: <code className="text-[10px]">{fromPreview}</code>
                </span>
              )}
              {fromShape === 'invalid' && (
                <span className="text-red-600">
                  Use a first name or an address on @{CAMPAIGN_SENDER_DOMAIN}.
                </span>
              )}
              <div className="text-gray-400 mt-0.5">
                Marketing campaigns always use the isolated news domain; replies still go to the
                canonical inbox.
              </div>
            </div>
          </Field>

          <Field label="Body" required>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              rows={7}
              className="w-full px-3.5 py-2.5 text-base sm:text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow leading-relaxed"
              placeholder="Hey {firstname} — wanted to share something I think you'd find useful…"
              required
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="CTA text">
              <input
                type="text"
                value={ctaText}
                onChange={(e) => setCtaText(e.target.value)}
                className="w-full h-11 px-3.5 text-base sm:text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow"
                placeholder="See what's new"
              />
            </Field>
            <Field label="CTA link" hint="site path · full URL · mailto">
              <input
                type="text"
                value={ctaPath}
                onChange={(e) => setCtaPath(e.target.value)}
                className="w-full h-11 px-3.5 text-base sm:text-sm text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#94c2dc] focus:border-[#94c2dc] transition-shadow font-mono"
                placeholder="/positive-ev or mailto:support@bookiebrawler.com"
              />
            </Field>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm text-gray-700">
                <span className="font-semibold tabular-nums">
                  {recipientCount.toLocaleString()}
                </span>{' '}
                recipients ready
              </div>
              {preview && preview.emails.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowEmails((v) => !v)}
                  className="text-xs text-[#0b3a55] hover:underline"
                >
                  {showEmails ? 'Hide sample' : `Show first ${preview.emails.length}`}
                </button>
              )}
            </div>
            {showEmails && preview && (
              <ul className="mt-2 space-y-0.5 max-h-32 overflow-y-auto font-mono text-[11px] text-gray-600">
                {preview.emails.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Test send */}
          <div className="border border-dashed border-[#94c2dc]/70 bg-[#eef7fb] rounded-lg p-3.5">
            <div className="text-[11px] uppercase tracking-wide text-[#0b3a55] font-semibold mb-1.5">
              Test send
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={testAddress}
                onChange={(e) => setTestAddress(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 h-10 px-3 text-sm text-gray-900 placeholder-gray-400 border border-[#94c2dc]/70 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#94c2dc]"
              />
              <button
                type="button"
                onClick={handleTestSend}
                disabled={!canSubmit || testSending}
                className="h-10 px-4 text-sm font-medium text-white bg-[#01183a] rounded-md hover:bg-[#09264f] disabled:bg-slate-300 disabled:text-slate-700 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {testSending ? 'Sending…' : 'Send test'}
              </button>
            </div>
            {testResult && (
              <div
                className={`mt-2 text-xs ${
                  testResult.ok ? 'text-emerald-700' : 'text-red-700'
                }`}
              >
                {testResult.message}
              </div>
            )}
            <div className="text-[10px] text-[#0b3a55]/70 mt-1.5">
              Runs through the real Resend path — confirms deliverability + visual fidelity in one click.
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}
        </form>

        {/* ── Preview column ──────────────────────────────────────── */}
        <div
          className={`bg-gray-50/40 ${
            activeTab === 'preview' ? 'block' : 'hidden'
          } lg:block`}
        >
          <EmailPreviewPanel body={previewBody} disabled={!subject.trim() || !bodyText.trim()} />
        </div>
      </div>
    </ModalShell>
  );
};

const Field: React.FC<{
  label: string;
  hint?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, hint, required, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {hint && <span className="text-[10px] text-gray-400 text-right">{hint}</span>}
    </div>
    {children}
  </div>
);

export default StartCampaignModal;
